import { useEffect, useState } from "react";
import axios from "axios";
import { FiUsers, FiHeart, FiActivity } from "react-icons/fi";
import UserGrowthChart from "../components/Charts/UserGrowthChart";
import { FaFemale, FaMale } from "react-icons/fa";
import { IoStatsChart } from "react-icons/io5";
import { MdOutlineAutoGraph } from "react-icons/md";

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    newUsersToday: 0,
    newUsersThisMonth: 0,
  });
  const [count, setcount] = useState();
  const [recentUsers, setRecentUsers] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [years] = useState([currentYear, currentYear - 1, currentYear - 2]);

  useEffect(() => {
    fetchDashboardData(selectedYear);
  }, [selectedYear]);

  const fetchDashboardData = async (year = selectedYear) => {
    try {
      setLoading(true);

      const [analyticsRes, usersRes, growthRes, genderCount] = await Promise.all([
        axios.get("/api/v1/admin/analytics/dashboard"),
        axios.get("/api/v1/admin/users"),
        axios.get(`/api/v1/admin/analytics/users?year=${year}`),
        axios.get(`/api/v1/user/getGender`),
      ]);

      if (analyticsRes.data.success) {
        setStats(analyticsRes.data.data);
      }
      if (genderCount.data.success) {
        setcount(genderCount.data.data);
      }

      if (usersRes.data.success) {
        const users = usersRes.data.data || [];
        setRecentUsers(users.slice(0, 5));
      }

      if (growthRes.data.success) {
        setUserGrowth(growthRes.data.data.monthlyGrowth || []);
      }
    } catch (error) {
      console.error("Dashboard Error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="relative flex items-center justify-center">
          <div className="w-14 h-14 rounded-full border-4 border-cyan-500/20 border-t-cyan-400 animate-spin" />
          <div className="absolute inset-0 rounded-full blur-md bg-cyan-400/25 animate-pulse" />
        </div>
      </div>
    );
  }

  const filteredUsers =
    activeTab === "verified"
      ? recentUsers.filter((user) => user.isProfileCompleted)
      : recentUsers;

  const statCards = [
    {
      label: "Total Users",
      value: stats.totalUsers,
      icon: <FiUsers />,
      accentColor: "#38bdf8",
      from: "rgba(56, 189, 248, 0.2)",
      to: "rgba(14, 116, 144, 0.05)",
      border: "rgba(56, 189, 248, 0.3)",
      glow: "rgba(56, 189, 248, 0.25)",
    },
    {
      label: "Active Users",
      value: stats.activeUsers,
      icon: <FiHeart />,
      accentColor: "#2dd4bf",
      from: "rgba(45, 212, 191, 0.2)",
      to: "rgba(13, 148, 136, 0.05)",
      border: "rgba(45, 212, 191, 0.3)",
      glow: "rgba(45, 212, 191, 0.25)",
    },
    {
      label: "Male Users",
      value: count?.male || 0,
      icon: <FaMale />,
      accentColor: "#60a5fa",
      from: "rgba(96, 165, 250, 0.2)",
      to: "rgba(37, 99, 235, 0.05)",
      border: "rgba(96, 165, 250, 0.3)",
      glow: "rgba(96, 165, 250, 0.25)",
    },
    {
      label: "Female Users",
      value: count?.female || 0,
      icon: <FaFemale />,
      accentColor: "#38bdf8",
      from: "rgba(6, 182, 212, 0.22)",
      to: "rgba(125, 211, 252, 0.05)",
      border: "rgba(6, 182, 212, 0.35)",
      glow: "rgba(6, 182, 212, 0.3)",
    },
  ];

  return (
    <div className="space-y-7 text-cyan-50 p-2 md:p-4">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-2 border-b border-cyan-500/15">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 via-sky-300 to-white">
            Dashboard Overview
          </h1>
          <p className="text-xs sm:text-sm text-cyan-200/60 mt-1">
            Real-time metrics, user growth, and active engagement
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-2 text-xs px-3.5 py-1.5 rounded-full font-medium bg-cyan-950/60 border border-cyan-400/30 text-cyan-300 backdrop-blur-md shadow-[0_0_15px_rgba(6,182,212,0.15)]">
            <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse shadow-[0_0_8px_#22d3ee]" />
            Live Network
          </span>
        </div>
      </div>

      {/* TOP STATS */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        {statCards.map((s) => (
          <div
            key={s.label}
            className="relative overflow-hidden rounded-2xl p-5 backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl"
            style={{
              background: `linear-gradient(135deg, ${s.from}, ${s.to})`,
              border: `1px solid ${s.border}`,
              boxShadow: `0 8px 30px rgba(2, 6, 23, 0.5), 0 0 20px ${s.glow}, inset 0 1px 1px rgba(255, 255, 255, 0.1)`,
            }}
          >
            {/* Water drop light blur effect */}
            <div
              className="absolute -top-10 -right-10 w-32 h-32 rounded-full pointer-events-none blur-2xl"
              style={{ background: s.glow }}
            />

            <div className="relative flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wider text-cyan-200/70 mb-2">
                  {s.label}
                </p>
                <p
                  className="font-black tracking-tight"
                  style={{
                    fontSize: "2.15rem",
                    lineHeight: 1.1,
                    color: s.accentColor,
                    textShadow: `0 0 18px ${s.glow}`,
                  }}
                >
                  {Number(s.value || 0).toLocaleString()}
                </p>
              </div>

              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-xl flex-shrink-0 backdrop-blur-md"
                style={{
                  background: "rgba(2, 12, 34, 0.55)",
                  color: s.accentColor,
                  border: `1px solid ${s.border}`,
                  boxShadow: `0 4px 15px rgba(0,0,0,0.3), inset 0 1px 1px rgba(255,255,255,0.1)`,
                }}
              >
                {s.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* USER GROWTH + ACTIVITY */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Growth */}
        <div
          className="rounded-2xl p-6 lg:col-span-2 backdrop-blur-xl relative overflow-hidden"
          style={{
            background: "linear-gradient(160deg, rgba(6, 24, 60, 0.75) 0%, rgba(3, 13, 36, 0.85) 100%)",
            border: "1px solid rgba(56, 189, 248, 0.2)",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.45), inset 0 1px 1px rgba(255, 255, 255, 0.08)",
          }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg bg-cyan-500/15 border border-cyan-400/30 text-cyan-300 shadow-[0_0_15px_rgba(6,182,212,0.2)]">
                <MdOutlineAutoGraph />
              </div>
              <div>
                <h2 className="text-base font-bold text-white tracking-wide">User Growth</h2>
                <p className="text-xs text-cyan-200/60">Monthly registered member analytics</p>
              </div>
            </div>

            <div className="min-w-[130px]">
              <select
                value={selectedYear}
                onChange={(e) => {
                  const y = Number(e.target.value);
                  setSelectedYear(y);
                  fetchDashboardData(y);
                }}
                className="w-full bg-cyan-950/70 text-cyan-100 text-xs font-semibold rounded-xl px-3 py-2 border border-cyan-400/30 focus:outline-none focus:border-cyan-300 backdrop-blur-md cursor-pointer transition shadow-inner"
              >
                {years.map((yr) => (
                  <option key={yr} value={yr} className="bg-[#04112e] text-white">
                    {yr}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="min-h-[300px]">
            <UserGrowthChart rawGrowth={userGrowth} selectedYear={selectedYear} />
          </div>
        </div>

        {/* User Activity */}
        <div
          className="rounded-2xl p-6 backdrop-blur-xl flex flex-col justify-between"
          style={{
            background: "linear-gradient(160deg, rgba(6, 24, 60, 0.75) 0%, rgba(3, 13, 36, 0.85) 100%)",
            border: "1px solid rgba(56, 189, 248, 0.2)",
            boxShadow: "0 10px 40px rgba(0, 0, 0, 0.45), inset 0 1px 1px rgba(255, 255, 255, 0.08)",
          }}
        >
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg bg-sky-500/15 border border-sky-400/30 text-sky-300 shadow-[0_0_15px_rgba(56,189,248,0.2)]">
                <IoStatsChart />
              </div>
              <div>
                <h2 className="text-base font-bold text-white tracking-wide">User Activity</h2>
                <p className="text-xs text-cyan-200/60">Engagement snapshot</p>
              </div>
            </div>

            <div className="space-y-3">
              {[
                {
                  label: "Active Users Today",
                  value: stats.activeUsers,
                  accent: "#2dd4bf",
                  from: "rgba(45, 212, 191, 0.12)",
                },
                {
                  label: "Total Users",
                  value: stats.totalUsers,
                  accent: "#38bdf8",
                  from: "rgba(56, 189, 248, 0.12)",
                },
                {
                  label: "New Users This Month",
                  value: stats.newUsersThisMonth != null ? `+${stats.newUsersThisMonth}` : "+0",
                  accent: "#818cf8",
                  from: "rgba(129, 140, 248, 0.12)",
                },
                {
                  label: "New Users Today",
                  value: stats.newUsersToday != null ? `+${stats.newUsersToday}` : "+0",
                  accent: "#67e8f9",
                  from: "rgba(103, 232, 249, 0.12)",
                },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between rounded-xl px-4 py-3.5 backdrop-blur-md transition-transform hover:scale-[1.01]"
                  style={{
                    background: `linear-gradient(135deg, ${row.from}, rgba(4, 18, 48, 0.3))`,
                    border: "1px solid rgba(56, 189, 248, 0.14)",
                  }}
                >
                  <span className="text-xs font-medium text-cyan-200/80">{row.label}</span>
                  <span
                    className="font-extrabold text-base tracking-tight"
                    style={{ color: row.accent }}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* RECENT USERS TABLE */}
      <div
        className="rounded-2xl overflow-hidden backdrop-blur-xl mb-6"
        style={{
          background: "linear-gradient(180deg, rgba(6, 24, 60, 0.75) 0%, rgba(3, 13, 36, 0.9) 100%)",
          border: "1px solid rgba(56, 189, 248, 0.2)",
          boxShadow: "0 10px 40px rgba(0, 0, 0, 0.45), inset 0 1px 1px rgba(255, 255, 255, 0.08)",
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4 border-b border-cyan-500/15">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg bg-teal-500/15 border border-teal-400/30 text-teal-300 shadow-[0_0_15px_rgba(45,212,191,0.2)]">
              <FiUsers />
            </div>
            <div>
              <h2 className="text-base font-bold text-white tracking-wide">Recent Users</h2>
              <p className="text-xs text-cyan-200/60">Latest members to dive into Spoonland</p>
            </div>
          </div>

          {/* Liquid Tabs */}
          <div className="flex items-center gap-1.5 p-1 rounded-xl bg-cyan-950/60 border border-cyan-500/20 backdrop-blur-md">
            <button
              onClick={() => setActiveTab("all")}
              className={`text-xs font-semibold px-4 py-1.5 rounded-lg transition-all duration-200 ${
                activeTab === "all"
                  ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-[0_2px_10px_rgba(6,182,212,0.4)]"
                  : "text-cyan-200/70 hover:text-white"
              }`}
            >
              All
            </button>
            <button
              onClick={() => setActiveTab("verified")}
              className={`text-xs font-semibold px-4 py-1.5 rounded-lg transition-all duration-200 ${
                activeTab === "verified"
                  ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-[0_2px_10px_rgba(6,182,212,0.4)]"
                  : "text-cyan-200/70 hover:text-white"
              }`}
            >
              Verified
            </button>
          </div>
        </div>

        {filteredUsers.length === 0 ? (
          <div className="py-14 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-cyan-950/50 border border-cyan-400/20 flex items-center justify-center text-cyan-400 text-xl">
              <FiUsers />
            </div>
            <p className="font-semibold text-cyan-100 text-sm">No users found</p>
            <p className="text-xs text-cyan-300/50 mt-1">Check back soon for new registrations</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-cyan-500/10 text-cyan-300/70 uppercase tracking-wider">
                  <th className="py-3.5 pl-6 font-semibold">User</th>
                  <th className="py-3.5 px-4 font-semibold">Profile Completion</th>
                  <th className="py-3.5 px-4 font-semibold">Joined</th>
                  <th className="py-3.5 px-4 font-semibold">Gender</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cyan-500/10">
                {filteredUsers.map((user) => {
                  const initials = (user.fullName || "?")
                    .split(" ")
                    .map((n) => n[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase();

                  return (
                    <tr
                      key={user._id || user.id}
                      className="transition-colors hover:bg-cyan-500/[0.04]"
                    >
                      <td className="py-3.5 pl-6">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-xs bg-gradient-to-tr from-cyan-600 to-blue-500 text-white border border-cyan-300/30 shadow-[0_0_12px_rgba(6,182,212,0.25)] flex-shrink-0">
                            {initials}
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-cyan-50 truncate tracking-wide">
                              {user.fullName}
                            </p>
                            <p className="text-[11px] text-cyan-300/60 truncate font-mono">
                              {(() => {
                                const phone = String(user.phoneNumber || "").replace(/\D/g, "");
                                if (phone.length === 10) {
                                  return `+91 ${phone.slice(0, 5)} ${phone.slice(5)}`;
                                }
                                if (phone.length === 12 && phone.startsWith("91")) {
                                  const number = phone.slice(2);
                                  return `+91 ${number.slice(0, 5)} ${number.slice(5)}`;
                                }
                                return user.phoneNumber;
                              })()}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${
                            user.isProfileCompleted
                              ? "bg-teal-500/15 border-teal-400/30 text-teal-300"
                              : "bg-rose-500/15 border-rose-400/30 text-rose-300"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              user.isProfileCompleted ? "bg-teal-400" : "bg-rose-400"
                            }`}
                          />
                          {user.isProfileCompleted ? "Completed" : "Incomplete"}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-cyan-200/70 font-medium">
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString(undefined, {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })
                          : "—"}
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="inline-block px-2.5 py-0.5 rounded-lg text-[11px] font-semibold bg-cyan-950/70 border border-cyan-400/20 text-cyan-300 capitalize">
                          {user.gender || "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}